import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiTrash2, FiEdit, FiSliders } from 'react-icons/fi';
import { attributesApi } from '../services/api';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';

export default function ManageAttributes() {
  const navigate = useNavigate();
  const { selectedPortal } = useHubSpotPortals(); 
  
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    label: '',
    internal_name: '',
    attribute_type: 'text',
    options: []
  });

  const [newOption, setNewOption] = useState({ option_value: '', option_label: '' });

  useEffect(() => {
    loadAttributes();
  }, [selectedPortal]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAttributes = async () => {
    console.log('loading attributes');
    console.log('selectedPortal in loadAttributes:', selectedPortal);
    if (!selectedPortal?.portal_id) return;
    
    try {
      console.log('selectedPortal.portal_id', selectedPortal.portal_id);
      setLoading(true);
      const response = await attributesApi.fetchAttributes(selectedPortal.portal_id);
      setAttributes(response.attributes || []);
    } catch (err) {
      console.error('Error loading attributes:', err);
      setError('Failed to load attributes.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/settings');
  };

  const resetForm = () => {
    setFormData({
      label: '',
      internal_name: '',
      attribute_type: 'text',
      options: []
    });
    setNewOption({ option_value: '', option_label: '' });
  };

  const handleOpenAddModal = () => {
    //console.log("selectedPortal from useHubSpotPortals:", JSON.stringify(selectedPortal));
    resetForm();
    setShowAddModal(true);
  };

  const handleOpenEditModal = (attribute) => {
    setEditingAttribute(attribute);
    setFormData({
      label: attribute.label,
      internal_name: attribute.internal_name,
      attribute_type: attribute.attribute_type,
      options: attribute.options || []
    });
    setShowEditModal(true);
  };

  // ✅ Generic input handler
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAddOption = useCallback(() => {
    if (newOption.option_value && newOption.option_label) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, { 
          ...newOption, 
          sort_order: prev.options.length + 1 
        }]
      }));
      setNewOption({ option_value: '', option_label: '' });
    }
  }, [newOption]);

  const handleRemoveOption = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  }, []);

  const handleCreateAttribute = async () => {
    try {
      const attributeData = {
        portal_id: selectedPortal.portal_id, 
        ...formData
      };

      //console.log("user from useAuth:", JSON.stringify(user));
      //console.log('attributeData', JSON.stringify(attributeData));
      
      await attributesApi.createAttribute(attributeData);
      setShowAddModal(false);
      resetForm();
      loadAttributes();
    } catch (err) {
      console.error('Error creating attribute:', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to create attribute';
      alert(message);
    }
  };

  const handleUpdateAttribute = async () => {
    try {
      await attributesApi.updateAttribute(editingAttribute.id, formData);
      setShowEditModal(false);
      setEditingAttribute(null);
      resetForm();
      loadAttributes();
    } catch (err) {
      console.error('Error updating attribute:', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to update attribute';
      alert(message);
    }
  };

  const handleDeleteAttribute = async (attribute) => {
    if (!window.confirm(`Delete "${attribute.label}"? This action cannot be undone and will remove all associated agent data.`)) {
      return;
    }

    try {
      await attributesApi.deleteAttribute(attribute.id);
      loadAttributes();
    } catch (err) {
      console.error('Error deleting attribute:', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to delete attribute';
      alert(message);
    }
  };

  const needsOptions = formData.attribute_type === 'dropdown' || formData.attribute_type === 'multiselect';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition duration-200"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center mb-1">
              <FiSliders className="w-6 h-6 text-gray-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Manage Attributes</h1>
            </div>
            <p className="text-gray-600">Create and manage custom attributes for agents</p>
          </div>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md transition-colors"
        >
          <FiPlus className="w-4 h-4 mr-2" /> Create Attribute
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading attributes...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Attributes Table */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          {attributes.length === 0 ? (
            <div className="text-center py-12">
              <FiSliders className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No attributes created yet</p>
              <button
                onClick={handleOpenAddModal}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Create your first attribute
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attribute
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Options
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attributes.map((attribute) => (
                    <tr key={attribute.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {attribute.label}
                          </div>
                          <div className="text-sm text-gray-500">
                            {attribute.internal_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {attribute.attribute_type === 'radio_yesno' ? 'Yes/No' : 
                           attribute.attribute_type === 'multiselect' ? 'Multi-select' :
                           attribute.attribute_type.charAt(0).toUpperCase() + attribute.attribute_type.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {attribute.options?.length > 0 ? `${attribute.options.length} options` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {attribute.created_at ? new Date(attribute.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleOpenEditModal(attribute)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                            title="Edit attribute"
                          >
                            <FiEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAttribute(attribute)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                            title="Delete attribute"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AttributeModal
          isEdit={false}
          formData={formData}
          setFormData={setFormData}
          newOption={newOption}
          setNewOption={setNewOption}
          handleInputChange={handleInputChange}
          handleAddOption={handleAddOption}
          handleRemoveOption={handleRemoveOption}
          needsOptions={needsOptions}
          closeModal={() => { setShowAddModal(false); resetForm(); }}
          handleSave={handleCreateAttribute}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <AttributeModal
          isEdit={true}
          formData={formData}
          setFormData={setFormData}
          newOption={newOption}
          setNewOption={setNewOption}
          handleInputChange={handleInputChange}
          handleAddOption={handleAddOption}
          handleRemoveOption={handleRemoveOption}
          needsOptions={needsOptions}
          closeModal={() => { setShowEditModal(false); setEditingAttribute(null); resetForm(); }}
          handleSave={handleUpdateAttribute}
        />
      )}
    </div>
  );
}

// ✅ Memoized Modal Component
const AttributeModal = memo(function AttributeModal({
  isEdit,
  formData,
  setFormData,
  newOption,
  setNewOption,
  handleInputChange,
  handleAddOption,
  handleRemoveOption,
  needsOptions,
  closeModal,
  handleSave
}) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? 'Edit Attribute' : 'Create New Attribute'}
        </h2>

        {/* Label */}
        <label className="block mb-2 text-sm font-medium">Label *</label>
        <input
          type="text"
          name="label"
          value={formData.label}
          onChange={handleInputChange}
          placeholder="e.g., State Certifications"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        {/* Internal Name */}
        <label className="block mb-2 text-sm font-medium">Internal Name *</label>
        <input
          type="text"
          name="internal_name"
          value={formData.internal_name}
          onChange={handleInputChange}
          placeholder="e.g., state_certifications"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mb-4">Letters, numbers, and underscores only</p>

        {/* Attribute Type */}
        <label className="block mb-2 text-sm font-medium">Attribute Type *</label>
        <select
          name="attribute_type"
          value={formData.attribute_type}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="text">Text Input</option>
          <option value="dropdown">Dropdown Select</option>
          <option value="multiselect">Multiple Select</option>
          <option value="radio_yesno">Radio Button (Yes/No)</option>
        </select>

        {/* Options (for dropdown and multiselect) */}
        {needsOptions && (
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Options *</label>
            
            {/* Add new option */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <input
                type="text"
                value={newOption.option_value}
                onChange={(e) => setNewOption(prev => ({ ...prev, option_value: e.target.value }))}
                placeholder="Value (e.g., CA)"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                value={newOption.option_label}
                onChange={(e) => setNewOption(prev => ({ ...prev, option_label: e.target.value }))}
                placeholder="Label (e.g., California)"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={handleAddOption}
              disabled={!newOption.option_value || !newOption.option_label}
              className="mb-3 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-300"
            >
              Add Option
            </button>

            {/* Options list */}
            {formData.options.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                {formData.options.map((option, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-sm">
                      <strong>{option.option_value}</strong> - {option.option_label}
                    </span>
                    <button
                      onClick={() => handleRemoveOption(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modal Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!formData.label || !formData.internal_name || (needsOptions && formData.options.length === 0)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
          >
            {isEdit ? 'Update Attribute' : 'Create Attribute'}
          </button>
        </div>
      </div>
    </div>
  );
});
